import { ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { StoreReviewsSection } from "../components/StoreReviewsSection";
import { HERO_SLIDES, NOW_LIVE_CARDS } from "../lib/siteContent";
import { applySeo } from "../lib/seo";

const preloadImage = (src) => {
  if (!src || typeof window === "undefined") return;
  const image = new window.Image();
  image.decoding = "async";
  image.src = src;
};

export const HomePage = () => {
  const [activeSlide, setActiveSlide] = useState(0);
  const [missingNowLiveImages, setMissingNowLiveImages] = useState({});
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchStartTimeRef = useRef(0);
  const suppressHeroLinkClickRef = useRef(false);

  useEffect(() => {
    applySeo({
      title: "Eternal Order | Identity-driven apparel inspired by Sanatana philosophy",
      description: "Premium, cinematic, dark-themed fashion ecommerce experience for Eternal Order.",
    });
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const totalSlides = HERO_SLIDES.length;
    const nextSlide = HERO_SLIDES[(activeSlide + 1) % totalSlides];
    const prevSlide = HERO_SLIDES[(activeSlide - 1 + totalSlides) % totalSlides];
    preloadImage(nextSlide?.image);
    preloadImage(prevSlide?.image);
  }, [activeSlide]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const warmAllSlides = () => {
      HERO_SLIDES.forEach((slide, index) => {
        if (index !== 0) preloadImage(slide.image);
      });
    };

    if ("requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(() => warmAllSlides(), { timeout: 1200 });
      return () => window.cancelIdleCallback(idleId);
    }

    const timeoutId = window.setTimeout(warmAllSlides, 300);
    return () => window.clearTimeout(timeoutId);
  }, []);

  const slide = useMemo(() => HERO_SLIDES[activeSlide], [activeSlide]);
  const goPrev = () => setActiveSlide((prev) => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const goNext = () => setActiveSlide((prev) => (prev + 1) % HERO_SLIDES.length);

  const onHeroTouchStart = (event) => {
    const touch = event.touches?.[0];
    if (!touch) return;
    touchStartXRef.current = touch.clientX;
    touchStartYRef.current = touch.clientY;
    touchStartTimeRef.current = Date.now();
  };

  const onHeroTouchEnd = (event) => {
    const touch = event.changedTouches?.[0];
    if (!touch) return;

    const deltaX = touch.clientX - touchStartXRef.current;
    const deltaY = touch.clientY - touchStartYRef.current;
    const elapsed = Date.now() - touchStartTimeRef.current;
    const horizontalDistance = Math.abs(deltaX);
    const verticalDistance = Math.abs(deltaY);

    const isHorizontalSwipe = horizontalDistance >= 44 && horizontalDistance > verticalDistance;
    const isReasonableGestureTime = elapsed <= 700;
    if (!isHorizontalSwipe || !isReasonableGestureTime) return;

    suppressHeroLinkClickRef.current = true;
    if (deltaX < 0) {
      goNext();
      return;
    }
    goPrev();
  };

  const onHeroSlideLinkClick = (event) => {
    if (!suppressHeroLinkClickRef.current) return;
    event.preventDefault();
    suppressHeroLinkClickRef.current = false;
  };

  return (
    <div className="pb-16" data-testid="home-page">
      <section className="border-b border-white/10 bg-[#04060d]" data-testid="home-hero-section">
        <div className="mx-auto w-full max-w-[1400px] overflow-hidden px-4 py-6 sm:px-6 lg:px-8">
          <div
            className="relative overflow-hidden rounded-2xl border border-white/10 bg-[#02040a] touch-pan-y"
            onTouchStart={onHeroTouchStart}
            onTouchEnd={onHeroTouchEnd}
            data-testid="home-hero-frame"
          >
            <Link to={slide.to} onClick={onHeroSlideLinkClick} className="block" data-testid="home-hero-slide-link">
              <div className="relative mx-auto w-full max-w-[980px]" data-testid="home-hero-slide-panel">
                <img
                  src={slide.image}
                  alt={`Hero slide ${activeSlide + 1}`}
                  className="h-auto max-h-[70vh] w-full object-cover object-center transition-opacity duration-200"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  data-testid="home-hero-carousel-image"
                />
              </div>
            </Link>

            <div className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between px-3 sm:px-4">
              <button
                onClick={goPrev}
                className="pointer-events-auto rounded-full border border-white/20 bg-black/35 p-2 text-white/85 transition hover:bg-black/45"
                data-testid="home-hero-prev-button"
              >
                <ChevronLeft size={14} />
              </button>
              <button
                onClick={goNext}
                className="pointer-events-auto rounded-full border border-white/20 bg-black/35 p-2 text-white/85 transition hover:bg-black/45"
                data-testid="home-hero-next-button"
              >
                <ChevronRight size={14} />
              </button>
            </div>

            <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5" data-testid="home-hero-indicators">
              {HERO_SLIDES.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => setActiveSlide(index)}
                  className={`h-1 rounded-full transition-all ${index === activeSlide ? "w-7 bg-[#C2A45D]" : "w-3 bg-white/40"}`}
                  data-testid={`home-hero-indicator-${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-16 w-full max-w-[1240px] px-4 sm:px-6 lg:px-8" data-testid="home-now-live-section">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C2A45D]" data-testid="home-now-live-kicker">
            NOW LIVE
          </p>
          <h2 className="mt-3 text-3xl text-foreground sm:text-4xl lg:text-5xl" data-testid="home-now-live-heading">
            Fresh from the Eternal Order universe.
          </h2>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="home-now-live-grid">
          {NOW_LIVE_CARDS.map((card) => (
            <Link
              key={card.id}
              to={card.to}
              className="group mx-auto w-full max-w-[340px] overflow-hidden rounded-2xl border border-border bg-card"
              data-testid={`home-now-live-card-${card.id}`}
            >
              <div className="relative aspect-square overflow-hidden bg-[#05070D]" data-testid={`home-now-live-image-wrapper-${card.id}`}>
                {missingNowLiveImages[card.id] ? (
                  <div className="flex h-full w-full items-center justify-center bg-[#0a0d14]" data-testid={`home-now-live-coming-soon-${card.id}`}>
                    <span className="rounded-full border border-[#C6A85A]/70 px-3 py-1 text-xs uppercase tracking-[0.14em] text-[#C6A85A]">
                      Coming Soon
                    </span>
                  </div>
                ) : (
                  <img
                    src={card.image}
                    alt={card.name}
                    className="h-full w-full object-contain object-center transition duration-300 ease-in-out group-hover:scale-[1.04] group-hover:brightness-110"
                    loading="lazy"
                    decoding="async"
                    onError={() => setMissingNowLiveImages((prev) => ({ ...prev, [card.id]: true }))}
                    data-testid={`home-now-live-image-${card.id}`}
                  />
                )}
                {(card.id === "customize" || card.id === "acid") && (
                  <div
                    className="pointer-events-none absolute inset-x-0 bottom-0 p-4"
                    style={{
                      background:
                        "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0) 80%)",
                    }}
                    data-testid={`home-now-live-overlay-${card.id}`}
                  >
                    <p
                      className="text-sm font-semibold uppercase tracking-[0.12em] text-[#FFFFFF]"
                      data-testid={`home-now-live-overlay-title-${card.id}`}
                    >
                      {card.id === "customize" ? "CUSTOMIZE YOUR DRIP" : "ACID WASH PREMIUM"}
                    </p>
                    <p className="mt-1 text-xs text-[#C6A85A]" data-testid={`home-now-live-overlay-cta-${card.id}`}>
                      Shop Now →
                    </p>
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-20 w-full max-w-5xl px-4 text-center sm:px-6 lg:px-8" data-testid="home-philosophy-section">
        <p className="text-xs uppercase tracking-[0.22em] text-[#C2A45D]">OUR PHILOSOPHY</p>
        <h2 className="mt-3 text-3xl tracking-[0.04em] text-foreground sm:text-4xl" data-testid="home-philosophy-heading">
          ANCIENT WISDOM. MODERN CANVAS.
        </h2>
        <p className="mx-auto mt-5 max-w-3xl text-sm leading-relaxed text-muted-foreground" data-testid="home-philosophy-body">
          Eternal Order brings Sanatana-inspired storytelling into modern apparel. Every piece is designed to carry identity,
          intention, and timeless energy into everyday style.
        </p>
        <Link
          to="/about"
          className="mt-8 inline-flex rounded-full border border-[#C2A45D] px-6 py-3 text-sm font-semibold text-[#C2A45D] transition hover:bg-[#C2A45D] hover:text-[#1C1C1C]"
          data-testid="home-philosophy-cta"
        >
          READ OUR STORY
        </Link>
      </section>

      <section
        className="mx-auto mt-20 grid w-full max-w-[1400px] gap-6 px-4 sm:px-6 xl:grid-cols-2 lg:px-8"
        data-testid="home-highlights-section"
      >
        <div className="overflow-hidden rounded-3xl border border-border bg-card" data-testid="home-customize-highlight-card">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="p-8">
              <p className="text-xs uppercase tracking-[0.22em] text-[#C2A45D]">MAKE IT YOURS</p>
              <h3 className="mt-3 text-3xl text-foreground" data-testid="home-customize-highlight-heading">
                Customize Your Drip
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-testid="home-customize-highlight-body">
                Build your own combination from our available designs and create a tee that is uniquely yours.
              </p>
              <p className="mt-2 text-xs text-[#C2A45D]" data-testid="home-customize-highlight-pricing-note">
                Base price ₹799 · Double large front-and-back prints add ₹200
              </p>
              <Link
                to="/customize"
                className="mt-6 inline-flex rounded-full border border-[#C2A45D] bg-[#05070D] px-5 py-2.5 text-sm font-semibold text-[#F5F5F5] transition hover:bg-[#C2A45D] hover:text-[#1C1C1C]"
                data-testid="home-customize-cta"
              >
                START CUSTOMIZING
              </Link>
            </div>
            <div className="relative aspect-[4/5] md:aspect-auto">
              <img
                src="/assets/whats-new-customize-no-text.png"
                alt="Customize your Eternal Order tee"
                className="h-full w-full object-cover object-center"
                loading="lazy"
                decoding="async"
                data-testid="home-customize-highlight-image"
              />
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-border bg-card" data-testid="home-acid-wash-highlight-card">
          <div className="grid gap-0 md:grid-cols-2">
            <div className="p-8">
              <p className="text-xs uppercase tracking-[0.22em] text-[#C2A45D]">ACID WASH PREMIUM</p>
              <h3 className="mt-3 text-3xl text-foreground" data-testid="home-acid-wash-heading">
                Acid Wash Premium
              </h3>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground" data-testid="home-acid-wash-body">
                Plain, elevated, and built with attitude. Our premium acid-wash tees bring texture, depth, and effortless edge to
                everyday fits.
              </p>
              <Link
                to="/collections/acid-wash-premium"
                className="mt-6 inline-flex rounded-full border border-[#C2A45D] bg-[#05070D] px-5 py-2.5 text-sm font-semibold text-[#F5F5F5] transition hover:bg-[#C2A45D] hover:text-[#1C1C1C]"
                data-testid="home-acid-wash-cta"
              >
                EXPLORE ACID WASH
              </Link>
            </div>
            <div className="relative aspect-[4/5] md:aspect-auto">
              <img
                src="/assets/whats-new-acid-wash-premium-no-text.png"
                alt="Acid Wash Premium t-shirts"
                className="h-full w-full object-cover object-center"
                loading="lazy"
                decoding="async"
                data-testid="home-acid-wash-highlight-image"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto mt-20 w-full max-w-[1400px] px-4 sm:px-6 lg:px-8" data-testid="home-footer-cta-section">
        <div className="rounded-3xl border border-border bg-card/80 p-8 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C2A45D]" data-testid="home-footer-cta-kicker">
            Eternal Order
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground" data-testid="home-footer-cta-text">
            Premium spiritual streetwear. Designed with intent. Built for everyday expression.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/shop"
              className="inline-flex rounded-full border border-[#C2A45D] bg-[#05070D] px-5 py-2.5 text-sm font-semibold text-[#F5F5F5] transition hover:bg-[#C2A45D] hover:text-[#1C1C1C]"
              data-testid="home-footer-cta-shop-button"
            >
              SHOP THE DROP
            </Link>
            <Link
              to="/contact"
              className="inline-flex rounded-full border border-border px-5 py-2.5 text-sm font-semibold text-foreground transition hover:border-[#C2A45D] hover:text-[#C2A45D]"
              data-testid="home-footer-cta-contact-button"
            >
              CONTACT US
            </Link>
          </div>
        </div>
      </section>

      <StoreReviewsSection />
    </div>
  );
};
